<template>
  <div id="app" :class="appClasses">
    <div id="screenType" class="is-hidden-desktop"></div>
    <nav-bar v-if="!pageRoute"
        :class="{'theater-mode': theaterMode && !isFullscreen}"
        :is-logged-in="isLoggedIn"
        :is-room="isRoom"
        :user-profile="userProfile"
        @logout="logout"
        :show-sidenav="showSidenav"
        v-show="showNavbar"/>
    <main class="scroll" :class="{'theater-mode': theaterMode && !isFullscreen}" :style="mainStyle">
      <StatusBar v-if="!pageRoute && userProfile"
          :message="'Invite others to use TwoSeven and unlock special features. <a onclick=&quot;window.appBus.$emit(\'show-referral-wiki\')&quot;>Click here to learn more</a>'"
          classes="is-static"
          persist-duration="10d"/>
      <StatusBar v-if="!pageRoute && userProfile && userProfile.isPatron"
          :message="'Please use screenshare while the twoseven extension adapts to recent browser changes'"
          classes="is-link"
          persist-duration="1d"/>
      <StatusBar v-else-if="!pageRoute && userProfile"
          :message="'Screenshare is enabled on your account while twoseven extension adapts to recent browser changes'"
          classes="is-link"
          persist-duration="1d"/>
      <StatusBar v-if="!pageRoute" v-for="(entry, $index) of appStatus" :key="$index" v-bind="entry" v-show="!entry.hide" :storage="null" @close="$set(entry, 'hide', true)"/>
      <router-view ref="view" :key="$route.path" :class="{'section': !theaterMode}"/>
    </main>
    <app-footer v-if="!pageRoute" :is-patron="userProfile && userProfile.isPatron">
      <template v-slot:top-slot>
        <div id="chat-footer" v-show="isRoom && isLoggedIn && screenType === 'large'" class="hide-on-med-and-down">
          <text-chat ref="textChat" v-show="!theaterMode"/>
        </div>
      </template>
    </app-footer>

    <div v-if="!pageRoute" id="edit-profile-modal" class="modal">
      <div class="modal-content">
        <v-profile v-if="userProfile" ref="editProfile" :profile="userProfile" @reset-avatar="confirmResetAvatar">
          <template v-slot:content-slot>
            <div class="modal-footer" v-show="$refs.editProfile && $refs.editProfile.tab === 'profile'">
              <a data-value="yes" class="modal-action waves-effect waves-light primary waves-primary btn" @click="updateProfile" :class="{disabled: ($refs.editProfile && !$refs.editProfile.isUpdated)}"> Save </a>
              <a data-value="no" class="modal-action modal-close waves-effect waves-red btn-flat"> Cancel </a>
            </div>
          </template>
        </v-profile>
      </div>
    </div>

    <div v-if="!pageRoute" id="reset-avatar-modal" class="modal">
      <div class="modal-content">
        <h4>Avatar Reset</h4>
        <p>Are you sure you want to reset your avatar to the default?</p>
      </div>
      <div class="modal-footer">
        <a data-value="yes" class="modal-action modal-close waves-effect waves-primary btn-flat" @click="resetAvatar"> Yes </a>
        <a data-value="no" class="modal-action modal-close waves-effect waves-red btn-flat"> No </a>
      </div>
    </div>

    <div v-if="!pageRoute" id="link-patron-modal" class="modal">
      <div class="modal-content">
        <div class="columns">
          <div class="column is-offset-2 is-8">
            <h4 class="has-text-centered title is-size-4">Link Your Supporter Email</h4>
            <h4 class="subtitle is-size-6">
              If you're already a supporter but are missing your privileges, link your supporter email ID to your TwoSeven account.
            </h4>
            <div class="field">
              <label class="label">TwoSeven email</label>
              <div class="field is-flex" style="flex: 1;">
                <div class="control" style="flex: 1;">
                  <input type="email" class="input browser-default" disabled :value="userProfile ? userProfile.email : ''">
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label">Patreon/Ko-Fi email</label>
              <div class="field has-addons is-flex" style="flex: 1;">
                <div class="control" style="flex: 1;">
                  <input id="link-patron-email" v-model.trim="linkPatronData.input" type="email" :class="{'is-danger': linkPatronData.disabled}" class="input is-primary browser-default" placeholder="Your supporter email ID" @input="validateEmail" @keyup.enter="linkPatronEmail">
                </div>
                <div class="control">
                  <a class="button secondary" :class="{'is-loading': linkPatronData.busy}" :disabled="linkPatronData.disabled" @click="linkPatronEmail">Link</a>
                </div>
              </div>
            </div>
            <div v-if="linkPatronData.extraInfo">
              <vue-showdown class="has-text-warning" :markdown="linkPatronData.extraInfo"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal v-if="!pageRoute && userProfile" :has-footer="false" id="referral-wiki-modal" class="scroll" ref="referralModal">
      <template slot="modal-content">
        <ReferralWiki :user-profile="userProfile"/>
      </template>
    </modal>

    <modal v-if="!pageRoute" :has-footer="false" id="redesign-wiki-modal" class="scroll" ref="redesignModal">
      <template slot="modal-content">
        <RedesignWiki :user-profile="userProfile"/>
      </template>
    </modal>

    <modal v-if="!pageRoute" :has-footer="false" :dismissible="false" ending-top="30%" id="idle-check-modal" ref="idleCheckModal">
      <template slot="modal-content">
        <div class="columns">
          <div class="column has-text-centered">
            <div class="title">
              <h5 class="title is-size-5">Knock Knock~</h5>
              <h6 class="subtitle is-size-6">Are you there?</h6>
            </div>
            <div class="">
              We've noticed that you've been idle for a while. Click the button below to keep your session active. <br>
              <div v-if="idleCheck.expiresAt && idleCheck.now" class="has-text-warning">
                You will be logged out in {{ Math.floor((idleCheck.expiresAt - idleCheck.now) / 1000) }} seconds
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column is-12 has-text-right">
            <button class="btn waves waves-effect waves-primary primary modal-close" @click="resetIdleTimer">I'm here</button>
          </div>
        </div>
      </template>
    </modal>

    <WhatsNew v-if="isLoggedIn && false"/>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { triggerEvent, toast } from '@/js/utils.js'
import { v4 as uuidv4 } from 'uuid'
import throttle from 'lodash/throttle.js'

import AppBus from '@C/bus/app-bus.vue'
import ExtensionApp from '@C/extension-app.vue'
import UserStylesheetMixin from '@C/user-stylesheet-mixin.vue'
import SocketEventsMixin from '@C/socket-events-mixin.vue'
import initializeVuexVarsFromLocalStorage from '@/js/local-storage-backed-vuex.js'
import Videoshare from '@/js/videoshare.js'

const userGestureEvents = [
  'mousemove',
  'click',
  'keyup'
]

export default {
  name: 'app',
  extends: ExtensionApp,
  mixins: [UserStylesheetMixin, SocketEventsMixin],
  components: {
    'app-footer': () => import(/* webpackChunkName: "footer" */ '@C/footer.vue'),
    'nav-bar': () => import(/* webpackChunkName: "navbar" */ '@C/navbar.vue'),
    'text-chat': () => import(/* webpackChunkName: "text-chat" */ '@C/text-chat/text-chat.vue'),
    'v-profile': () => import(/* webpackChunkName: "profile" */ '@C/home-page-components/profile.vue'),
    WhatsNew: () => import(/* webpackChunkName: "whats-new" */ '@RC/whats-new.vue'),
    StatusBar: () => import(/* webpackChunkName: "status-bar" */ '@C/status-bar.vue'),
    Modal: () => import(/* webpackChunkName: "modal" */ '@C/common/modal.vue'),
    ReferralWiki: () => import(/* webpackChunkName: "referral-wiki" */ '@C/referral-wiki.vue'),
    RedesignWiki: () => import(/* webpackChunkName: "redesign-wiki" */ '@C/redesign-wiki.vue'),
    VueShowdown: () => import(/* webpackChunkName: "vue-showdown" */ 'vue-showdown/src/components/vue-showdown')
  },
  computed: {
    ...mapGetters([
      'appStatus',
      'showNavbar',
      'showFooter',
      'showSidenav',
      'isLoggedIn',
      'socket',
      'onSocketAvailable',
      'screenType',
      'userProfile',
      'activeTab',
      'theme',
      'videoSettings',
      'theaterMode',
      'isFullscreen',
      'showFooter',
      'accessToken',
      'geoData',
      'idleCheck'
    ]),
    pageRoute () {
      return (this.$route.path.startsWith('/help/') || this.$route.path.startsWith('/pages/'))
    },
    isRoom: {
      get: function () {
        return this.$store.getters.isRoom
      },
      set: function (v) {
        this.$store.commit('isRoom', v)
        this.$emit('is-room', v)
      }
    },
    geoData: {
      get: function () {
        return this.$store.getters.geoData
      },
      set: function (v) {
        this.$store.commit('geoData', v)
      }
    },
    showSidenav () {
      if (this.$route.name === 'landingPage') {
        return false
      }
      if (this.screenType === 'med-and-down') {
        return true
      }
      if (this.theaterMode) {
        return true
      }
      return false
    },
    appClasses () {
      const classes = new Set([this.theme])
      if (this.theaterMode) {
        classes.add('dark')
        classes.add('theater-mode')
      }
      if (this.isFullscreen) {
        // Fullscreen is dark
        classes.add('dark')
      }
      return [...classes]
    },
    mainStyle () {
      if (!this.showFooter) {
        return {
          'margin-bottom': 'unset'
        }
      }
      return null
    },
    sessionID: {
      get () {
        return this.$store.getters.sessionID
      },
      set (v) {
        this.$store.commit('sessionID', v)
      }
    }
  },
  data () {
    return {
      editProfileModal: undefined,
      resetAvatarModal: undefined,
      linkPatronData: {
        input: '',
        disabled: false,
        busy: false,
        extraInfo: ''
      }
    }
  },
  watch: {
    async accessToken (v) {
      if (!v) {
        return this.$cookie.delete('auth-accessToken')
      }
      const { default: jwtDecode } = await import(/* webpackChunkName: "jwt-decode" */ 'jwt-decode')
      const decoded = jwtDecode(v)
      const remainingTime = decoded.exp - ((Date.now() / 1000) | 0)
      this.$cookie.set('auth-accessToken', v, { expires: `${remainingTime}s` })
    },
    'userProfile.avatar' () {
    },
    '$route.fullPath' (v) {
      this.initializeVuex()
    },
    'videoSettings.plyr__captions': {
      handler (v, o) {
        this.rebuildUserStylesheet('videoSettings__plyr__captions', v)
      },
      deep: true
    }
  },
  methods: {
    ...mapActions([
      'logout',
      'resetIdleTimer'
    ]),
    checkForExtension () {
      return new Promise(resolve => {
        const onNoExtension = (msg) => {
          console.log(msg)
          const version = '0.0.0'
          this.$store.getters.extensionVersionPromise.resolve(version)
          this.$store.commit('extensionVersion', version)
          Videoshare.extensionVersion = version
          resolve()
        }
        const extDiv = document.querySelector('#twoseven-ext-detection')
        if (extDiv) {
          const timeout = setTimeout(() => {
            onNoExtension('No extension callback detected despite extension div being present. This could be a problem')
          }, 3000)
          window.addEventListener('ext:check-result', async (e) => {
            clearTimeout(timeout)
            const { detail: { data } } = e
            const { version } = data
            this.$store.commit('extensionVersion', version)
            this.$store.getters.extensionVersionPromise.resolve(version)
            Videoshare.extensionVersion = version
            console.log(`Detected extension v${version}`)
            // Get extension's clientID and settings
            // We don't do an await here because < v2.1.5.42 of extension don't support these requests
            Promise.all([
              this.newGenericRequest('ext:getClientID'),
              this.newGenericRequest('ext:getSettings')
            ]).then(results => {
              const [
                clientID,
                { keys, settings: settingsData }
              ] = results
              this.$store.getters.extensionConfig.clientID = clientID
              const { $store: { getters: { extensionConfig: { settings } } } } = this
              this.$set(settings, 'keys', keys)
              this.$set(settings, 'data', settingsData)
            })
            resolve()
          }, { once: true })
          triggerEvent(window, 'ext:check')
        } else {
          onNoExtension('No extension detected')
        }
      })
    },
    onResize: function () {
      const screenTypeEl = document.querySelector('#screenType')
      const isLarge = (screenTypeEl.style.display || window.getComputedStyle(screenTypeEl, null).display) === 'none'
      // var isMedAndUp = $window.innerHeight > 500 || $window.innerWidth > 800;
      let newScreenType
      if (isLarge) {
        // We're on med-and-up
        if (this.screenType !== 'large') {
          newScreenType = 'large'
        }
      } else {
        // We're on med-and-down
        if (this.screenType !== 'med-and-down') {
          newScreenType = 'med-and-down'
          // TODO: We need to be able to attach listeners to this event
          // Hide showingDiscussion when switching to small screen
          if (this.activeTab && this.activeTab.onResize) {
            this.activeTab.onResize(this.$store, newScreenType)
          }
        }
      }
      if (newScreenType) {
        this.$store.commit('screenType', newScreenType)
        console.log(`Setting screenType=${this.screenType}`)
      }
    },
    afterUpdateProfile (response, closeModal = true) {
      if (response.status !== 'OK') {
        // Throw a toast reporting the error
        return toast(`Failed to update profile: ${JSON.stringify(response.messages)}`)
      }
      // Avatar and nickname are updated on updated-profile event handler in src/store/index.js
      if (closeModal) {
        this.editProfileModal.close()
      }
    },
    async updateProfile () {
      const { finalNickname: nickname } = this.$refs.editProfile
      const avatar = await this.$refs.editProfile.finalAvatar()
      const hasNicknameChanged = nickname !== this.userProfile.nickname
      const hasAvatarChanged = !!avatar
      if (hasNicknameChanged) {
        this.socket.emit('update-profile', {
          action: 'nickname',
          value: nickname
        }, hasAvatarChanged ? () => {} : this.afterUpdateProfile)
      }
      if (avatar) {
        this.socket.emit('update-profile', {
          action: 'avatar',
          value: avatar
        }, this.afterUpdateProfile)
      }
    },
    confirmResetAvatar () {
      this.resetAvatarModal.open()
    },
    resetAvatar () {
      this.socket.emit('update-profile', {
        action: 'avatar',
        value: null
      }, (response) => this.afterUpdateProfile(response, false))
    },
    async validateEmail (e) {
      const { target } = e
      const { linkPatronData } = this
      const { value } = target
      if (value === '') {
        linkPatronData.disabled = false
        return
      }
      const validatorMod = await import('email-validator')
      const { default: validator } = validatorMod
      if (validator.validate(value.trim())) {
        linkPatronData.disabled = false
      } else {
        linkPatronData.disabled = true
      }
    },
    async linkPatronEmail (e) {
      const { linkPatronData } = this
      const { input: email } = linkPatronData
      // Reset extraInfo
      linkPatronData.extraInfo = ''
      let erroredOut = false
      if (email === '') {
        // User trying to submit empty. Set disabled to true
        linkPatronData.disabled = true
        return
      }
      const validatorMod = await import('email-validator')
      const { default: validator } = validatorMod
      if (!validator.validate(email)) {
        return
      }
      linkPatronData.busy = true
      let message
      try {
        const response = await this.apiRequest('/api/link-patron-node', {
          body: {
            patronEmail: email
          }
        }, 'post')
        message = response.data
        // This was a success
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } catch (e) {
        message = e.response.data
        erroredOut = true
      } finally {
        linkPatronData.busy = false
      }
      // If this is the 1st of the month, then we show extra info to the user trying to link accounts stating that patreon is awful on the 1st of every month
      const { default: moment } = await import('moment')
      const now = moment()
      const nowMonth = now.month()
      const yesterday = now.subtract(1, 'day')
      const yesterdayMonth = yesterday.month()
      if (erroredOut && (yesterdayMonth !== nowMonth)) {
        // This is the first day of the month
        linkPatronData.extraInfo = 'If you began supporting us on Patreon today, please [let Patreon know](https://support.patreon.com/hc/en-us) that we have not received notification of your pledge yet.'
        return
      }
      toast(message)
    },
    initializeVuex (setupListeners = false) {
      initializeVuexVarsFromLocalStorage(this, {
        theme: {
          type: String,
          default: 'dark'
        },
        muteAudioNotifications: {
          type: Boolean,
          default: false
        },
        userJoinLeaveTextNotification: {
          type: Boolean,
          default: true
        },
        userJoinLeaveToastNotification: {
          type: Boolean,
          default: true
        },
        userJoinLeaveAudioNotification: {
          type: Boolean,
          default: false
        },
        defaultRoomTypeIsDictator: {
          type: Boolean,
          default: false
        },
        defaultRoomHasP2P: {
          type: Boolean,
          default: true
        },
        advancedOpts: {
          type: Object,
          module: 'advancedOptions'
        },
        videoSettings: {
          type: Object
        },
        showSyncVideoTip: {
          type: Boolean,
          module: 'room',
          default: true
        },
        theaterMode: {
          type: Boolean,
          default: false,
          module: 'room',
          mutation: '_theaterMode',
          action: 'toggleTheaterMode',
          check ({ app }) {
            return app.$route.name === 'roomPage'
          }
        },
        reactionsEnabled: {
          type: Boolean,
          default: true
        },
        reactionUrls: {
          type: Array
        },
        showFooter: {
          type: Boolean,
          default: true
        },
        showVideoTitleInTheaterMode: {
          type: Boolean,
          default: true,
          module: 'room'
        },
        geoData: {
          type: Object,
          default: undefined,
          duration: '12h'
        },
        recentlyUsedEmojis: {
          type: Array,
          module: 'textChat'
        },
        showRoomPropertiesOnRoomCreate: {
          type: Boolean,
          default: true
        }
      }, setupListeners)
    },
    onUserGesture: throttle(function (e) {
      try {
        this.userGestureVideo.load()
        this.userGestureVideo.src = this.userGestureVideoURL
        this.userGestureVideo.play().then(() => {
          delete this.userGestureVideo
          URL.revokeObjectURL(this.userGestureVideoURL)
          delete this.userGestureVideoURL
          this.$store.commit('firstInteractionOccurred', true)
          for (const evt of userGestureEvents) {
            window.removeEventListener(evt, this.onUserGesture)
          }
        }, () => {
        })
      } catch (e) {
      }
    }, 100),
    openReferralWiki () {
      this.$refs.referralModal.open()
    },
    onBeforeUnload () {
      sessionStorage.setItem('sessionID', this.sessionID)
    },
    onSocketInitialized (socket) {
      this.setupSocketIOCommonEvents(socket)
      this.$store.dispatch('getFriends', socket)
      socket.emit('get-app-status', null, this.updateAppStatus)
    }
  },
  updated: function () {
    // this.onAppReady()
  },
  created: function () {
  },
  async beforeMount () {
    const video = this.userGestureVideo = document.createElement('video')
    video.muted = false
    video.volume = 0.001
    const response = await fetch('/static/media/video-settings-preview.mp4')
    const blob = await response.blob()
    this.userGestureVideoURL = URL.createObjectURL(blob)

    for (const evt of userGestureEvents) {
      window.addEventListener(evt, this.onUserGesture)
    }
    AppBus.$on('socket-initialized', this.onSocketInitialized)
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.onResize)
    AppBus.$off('socket-initialized', this.onSocketInitialized)
  },
  // Must not specify beforeMount here since extension-app defines it
  async mounted () {
    const self = this
    // Generate a sessionID that persists until user leaves the SPA
    // Don't use sessionStorage for this, since we use this sessionID to determine
    // a unique ID to prevent multiple duplicate video boxes in simple-peer
    window.addEventListener('beforeunload', this.onBeforeUnload)

    const sessionStorageSessionID = sessionStorage.getItem('sessionID')
    if (sessionStorageSessionID) {
      this.sessionID = sessionStorageSessionID
      sessionStorage.removeItem('sessionID')
    } else {
      this.sessionID = uuidv4()
    }

    AppBus.$on('logout', function () {
      self.$router.push('/')
    })

    AppBus.$on('route:landing', () => {
      this.$router.push('/')
    })

    AppBus.$on('show-referral-wiki', this.openReferralWiki)
    AppBus.$on('show-redesign-wiki', () => this.$refs.redesignModal.open())
    AppBus.$on('show-idle-check-modal', () => this.$refs.idleCheckModal.open())
    AppBus.$on('idle-client', () => {
      // This should have been triggered with the idle-check-modal being visible
      // This event implies that the user is about to be soft-logged-out and kicked back to landing page
      // So hide the idle-check-modal if it is visible
      this.$refs.idleCheckModal.close()
    })

    this.initializeVuex(true)
    if (!this.geoData || !this.geoData.key) {
      try {
        const response = await fetch('/api/unsecured/geo')
        const geoData = await response.json()
        this.$store.commit('geoData', geoData)
      } catch (e) {
      }
    }

    await self.checkForExtension()

    this.editProfileModal = window.M.Modal.init(this.$el.querySelector('#edit-profile-modal'), {
      dismissible: true,
      onOpenStart () {
        const editProfile = self.$refs.editProfile
        editProfile.reset()
      },
      onOpenEnd () {
        window.M.updateTextFields()
      }
    })

    this.resetAvatarModal = window.M.Modal.init(this.$el.querySelector('#reset-avatar-modal'), {
      startingTop: '60%'
    })

    this.linkPatronModal = window.M.Modal.init(this.$el.querySelector('#link-patron-modal'), {
      dismissible: true,
      onOpenStart () {
      },
      onOpenEnd () {
        window.M.updateTextFields()
      }
    })

    AppBus.$on('link-patron-modal-open', () => this.linkPatronModal.open())
    AppBus.$on('edit-profile', () => this.editProfileModal.open())
    AppBus.$on('close-edit-profile', () => this.editProfileModal.close())

    window.addEventListener('resize', this.onResize)
    this.$on('request-onResize', this.onResize)
    this.onResize()

    this.$store.dispatch('initialize')
  }
}
</script>

<style lang="scss">
@import '@/style/theme-main.scss';
@import '@/style/theme-dark.scss';
@import '@twosevenxyz/plyr/src/sass/plyr.scss';
</style>

<style lang="sass">
  @import "bulma/sass/utilities/_all.sass"
  @import "bulma/sass/helpers/_all.sass"
  @import "bulma/sass/base/_all.sass"
  @import "bulma/sass/elements/_all.sass"
  @import "bulma/sass/form/_all.sass"
  @import "bulma/sass/layout/_all.sass"
  @import "bulma/sass/grid/_all.sass"
  // Now the components
  @import "bulma/sass/components/breadcrumb.sass"
  @import "bulma/sass/components/card.sass"
  @import "bulma/sass/components/dropdown.sass"
  @import "bulma/sass/components/level.sass"
  // @import "~bulma/sass/components/panel.sass"
  @import "bulma/sass/components/media.sass"
  @import "bulma/sass/components/menu.sass"
  @import "bulma/sass/components/message.sass"
  @import "bulma/sass/components/navbar.sass"
  @import "bulma/sass/components/pagination.sass"
  @import "bulma/sass/components/panel.sass"
  @import "bulma/sass/components/tabs.sass"
</style>

<style lang="scss">
@import './style/app.scss';
@import './style/themes.scss';
@import '@twosevenxyz/login-component/dist/style.css';

body {
  ::v-deep .login-root {
    .modal-content {
      &.slide-from-bottom-enter-active, &.modal-content.slide-from-bottom-leave-active {
        transition-duration: 0.4s !important;
        transition-timing-function: ease-in-out !important;
      }
      &.slide-from-bottom-enter, &.slide-from-bottom-leave-to {
        transition-duration: 0.4s !important;
        transition-timing-function: ease-in-out !important;
      }
    }
  }
}

.hidden {
  display: none;
}

#toast-container, .toasted-container {
  top: auto !important;
  left: auto !important;
  bottom: 12% !important;

  @include from($desktop) {
    right: 2%;
  }
  a {
    cursor: pointer;
  }

  .client-toast {
    border-radius: 20px !important;
    opacity: 0.6;
  }
  .client-toast-above-all {
    border-radius: 20px !important;
    opacity: 0.6;
    z-index: 2147483647 !important;
  }

  .toast-action.toast-close {
    margin-left: 0.5em;
    margin-right: -15px;
    color: white;
  }
}

#edit-profile-modal {
  width: 50%;
  top: 20% !important;
  @include until($fullhd) {
    width: 75%;
  }
  @include until($widescreen) {
    width: 80%
  }
  @include until($desktop) {
    width: 85%;
  }
  @include until($tablet) {
    width: 95%;
  }

  border-radius: 20px;
  overflow-y: hidden;
  > .modal-content {
    height: 520px;
    max-height: 70vh;
    @include until($desktop) {
      max-height: 90vh;
    }
    padding: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    .modal-footer {
      padding-right: 2em;
      background-color: #eaeaea;
    }

    *::-webkit-scrollbar-thumb {
      border-radius: 20px;
    }
    *::-webkit-scrollbar-track {
    }
    *::-webkit-scrollbar-track-piece {
      border-radius: 20px;
    }
  }
}

#reset-avatar-modal {
  width: 460px !important;
  border-radius: 5px;
  top: 30% !important;
  .modal-content {
    h4 {
      font-size: 1.6rem;
      margin-bottom: 0.2rem;
    }
  }
}

#referral-wiki-modal {
  width: 90%;
  @media only screen and (min-width: 2260px) {
    width: 65%;
  }
  @include until($widescreen) {
    width: 80%
  }
  @include until($desktop) {
    width: 90%;
  }
  @include until($tablet) {
    width: 95%;
  }
}

#idle-check-modal {
  width: 40%;
  @media only screen and (min-width: 2260px) {
    width: 30%;
  }
  @include until($widescreen) {
    width: 60%
  }
  @include until($desktop) {
    width: 90%;
  }
  @include until($tablet) {
    width: 95%;
  }
}

 #link-patron-modal {
  width: 40%;
  min-height: 420px;
  @media only screen and (min-width: 2260px) {
    width: 40%;
  }
  @include until($widescreen) {
    width: 60%
  }
  @include until($desktop) {
    width: 90%;
  }
  @include until($tablet) {
    width: 95%;
  }
}

.auth0-lock-opened {
  z-index: 10000 !important;
}

.row.nomargin {
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 0;
}

.help-info {
  // color: rgba(120, 120, 120, 1);
}

.resize-arrow {
  color: #aeaeae;
  position: absolute;
  bottom: -9px;
  right: -9px;
  z-index: 999;
  transform: rotate(-45deg);

  @include no-select;
}

#chat-footer #chat-section {
  display: flex;
  flex-direction: column;
  &.expanded {
    min-height: 40vh;
    max-height: 40vh;
    height: 40vh;
  }
}
</style>
